/**
 * Shared Altcha captcha set up.
 * Used by the QuestionForm and the LibChat widget.
 */
import 'altcha/external';

export const Pbkdf2Worker = class {
    constructor(workerUrl) {
        // Same-origin: load directly. Cross-origin: blob+importScripts, because browsers
        // enforce same-origin on the initial worker script URL even for module workers with
        // CORS headers. The embedding page's CSP (not LA's) governs blob: in that context.
        if (new URL(workerUrl).origin === window.location.origin) {
            return new Worker(new URL(workerUrl), { type: 'module' });
        }
        const blob = new Blob(
            [`importScripts(${JSON.stringify(workerUrl)})`],
            { type: 'text/javascript' },
        );
        const objectUrl = URL.createObjectURL(blob);
        const worker = new Worker(objectUrl);
        URL.revokeObjectURL(objectUrl);
        return worker;
    }
};

/**
 * Create an altcha widget in the container (replacing its contents).
 *
 * @param {HTMLElement} container
 * @param {Object} options
 * @param {string} options.host Hostname to load the challenge and worker from.
 * @param {function(string)} [options.onVerified] Called with the payload once verified.
 * @param {function()} [options.onExpired] Called when a verified payload expires.
 * @param {function()} [options.onError] Called if the widget errors (e.g. worker can't load).
 * @returns {HTMLElement} The altcha-widget element.
 */
export function createAltchaWidget(container, { host, onVerified, onExpired, onError } = {}) {
    globalThis.$altcha.algorithms.set('PBKDF2/SHA-256', () => new Pbkdf2Worker(`https://${host}/altcha-worker.js`));
    const widget = document.createElement('altcha-widget');
    widget.setAttribute('challenge', `https://${host}/form/altcha-challenge`);
    widget.setAttribute('name', 'altcha');
    widget.setAttribute('type', 'native');
    container.innerHTML = '';
    container.appendChild(widget);

    widget.addEventListener('statechange', (ev) => {
        const state = ev.detail?.state;
        if (state === 'verified' && onVerified) {
            onVerified(ev.detail.payload ?? '');
        } else if (state === 'expired' && onExpired) {
            onExpired();
        } else if (state === 'error' && onError) {
            onError();
        }
    });
    return widget;
}

/**
 * Reset a widget so the patron has to verify again.
 *
 * @param {HTMLElement|null} widget The altcha-widget element.
 */
export function resetAltchaWidget(widget) {
    if (widget && typeof widget.reset === 'function') {
        widget.reset();
    }
}
